import { UserRole } from '@kfolks/types'
import { useAuth } from '@/hooks/useAuth'
import { isAdmin, roleAtLeast } from './role-order.js'

export interface ViewerRole {
	role: UserRole
	userId: string
	isAdmin: boolean
	isSuperAdmin: boolean
	/**
	 * True while `useAuth()` is still resolving the current session from storage
	 * + `GET /auth/me`. Consumers gating access (`<RequireRole>`, `<Sidebar>`,
	 * menu items) MUST wait for `isLoading === false` before acting on the role,
	 * or they will see a transient `VIEWER` placeholder and redirect/hide
	 * incorrectly.
	 */
	isLoading: boolean
}

/**
 * Returns the authenticated viewer's role with derived admin flags.
 * `isAdmin` is true for both ADMIN and SUPER_ADMIN; `isSuperAdmin` is true only for SUPER_ADMIN.
 *
 * Returns a VIEWER placeholder when there is no authenticated user so consumers
 * always receive a well-typed object — route guards gate access separately.
 */
export function useViewerRole(): ViewerRole {
	const { user, isLoading } = useAuth()

	if (!user) {
		return {
			role: UserRole.VIEWER,
			userId: '',
			isAdmin: false,
			isSuperAdmin: false,
			isLoading,
		}
	}

	const role = user.role as UserRole
	return {
		role,
		userId: user.userId,
		isAdmin: isAdmin(role),
		isSuperAdmin: roleAtLeast(role, UserRole.SUPER_ADMIN),
		isLoading,
	}
}
